import { useEffect, useState, useCallback, useRef } from "react";
import { ShieldCheck, WarningCircle, XCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import client from "../lib/api";

// ---------------- Shared cache ----------------
// The badge is rendered many times per page (once per invoice/load row). We
// hit `/customers/credit-scores` ONCE per session and index by customer name.
// TTL 5 min so newly-created customers show up after a small delay.

const CACHE_TTL_MS = 5 * 60 * 1000;
let _cache = null;         // {ts, byName: Map<lowerName, score>}
let _pending = null;       // in-flight promise (dedupes concurrent first-loads)
const _listeners = new Set();

async function _fetchAll() {
  if (_cache && Date.now() - _cache.ts < CACHE_TTL_MS) return _cache;
  if (_pending) return _pending;
  _pending = (async () => {
    try {
      const { data } = await client.get("/customers/credit-scores");
      const byName = new Map();
      for (const c of data.customers || []) {
        byName.set((c.customer || "").trim().toLowerCase(), c);
      }
      _cache = { ts: Date.now(), byName };
      return _cache;
    } catch {
      // On error, cache an empty result briefly so we don't hammer the API
      _cache = { ts: Date.now(), byName: new Map() };
      return _cache;
    } finally {
      _pending = null;
      _listeners.forEach((fn) => fn());
    }
  })();
  return _pending;
}

/** Force a refresh — call after creating/editing an invoice so the badge
 *  reflects the change on the next paint. */
export function invalidateCustomerCreditCache() {
  _cache = null;
  _listeners.forEach((fn) => fn());
}

const BAND_META = {
  green: { icon: ShieldCheck, tone: "text-emerald-300", bg: "bg-emerald-500/15", border: "border-emerald-500/30", label: "Low risk" },
  amber: { icon: WarningCircle, tone: "text-amber-300", bg: "bg-amber-500/15", border: "border-amber-500/30", label: "Watch" },
  red:   { icon: XCircle, tone: "text-rose-300", bg: "bg-rose-500/15", border: "border-rose-500/40", label: "High risk" },
};

/**
 * Small inline risk badge for a customer name. Auto-hides when no scoring
 * data exists yet (e.g. brand-new customer with only a draft invoice). Clicks
 * navigate to the full credit page for that customer.
 *
 * Props:
 *   name (string, required) — the customer name to look up (case-insensitive)
 *   size ("sm"|"md", default "sm") — visual size
 *   showText (bool, default false) — when true, shows the band label text next to the icon
 */
export default function CustomerCreditBadge({ name, size = "sm", showText = false }) {
  const [score, setScore] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const mountedRef = useRef(true);

  const compute = useCallback(async (n) => {
    const cache = await _fetchAll();
    if (!mountedRef.current) return;
    const found = cache.byName.get((n || "").trim().toLowerCase());
    setScore(found || null);
    setLoaded(true);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    compute(name);
    // Re-render this instance when cache invalidates
    const listener = () => compute(name);
    _listeners.add(listener);
    return () => {
      mountedRef.current = false;
      _listeners.delete(listener);
    };
  }, [name, compute]);

  if (!loaded) {
    // Placeholder space so table row height stays stable
    return <span className="inline-block w-2 h-2 rounded-full bg-white/5" />;
  }
  if (!score || !score.band) return null;  // silently hide when no data

  const meta = BAND_META[score.band];
  if (!meta) return null;
  const Icon = meta.icon;

  const tooltip = `${meta.label} · ${score.reasons?.slice(0, 2).join(" · ") || "—"}`;

  if (!showText) {
    // Compact dot-only style — perfect for dense table rows
    return (
      <Link
        to={`/app/customer-credit`}
        title={tooltip}
        data-testid={`customer-credit-badge-${score.band}`}
        onClick={(e) => e.stopPropagation()}
        className={`inline-flex items-center justify-center rounded-full border ${meta.border} ${meta.bg} ${meta.tone} ${size === "md" ? "w-5 h-5" : "w-4 h-4"} hover:brightness-125 transition`}
      >
        <Icon size={size === "md" ? 12 : 10} weight="fill" />
      </Link>
    );
  }

  // Full pill with text label
  return (
    <Link
      to={`/app/customer-credit`}
      title={tooltip}
      data-testid={`customer-credit-badge-${score.band}`}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 rounded-full border ${meta.border} ${meta.bg} ${meta.tone} px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold hover:brightness-125 transition`}
    >
      <Icon size={10} weight="fill" />
      {meta.label}
    </Link>
  );
}
